import React, { useState } from 'react';
import { useScrollReveal } from './useScrollReveal';

export default function FeaturesSection() {
  const reveal = useScrollReveal();
  const [activeZone, setActiveZone] = useState('dessert');
  const [checkedTimelineItem, setCheckedTimelineItem] = useState(true);

  const zones = [
    { id: 'dessert', label: '🍰 Focal Table', desc: 'Tiered stands, linen runner, custom topper' },
    { id: 'backdrop', label: '📍 Photo Wall', desc: 'Balloon garland, custom vinyl banner, ring lighting' },
    { id: 'tablescape', label: '🍽️ Place Setting', desc: 'Woven placemats, bud vases, scripted place cards' },
    { id: 'bar', label: '🍹 Beverage Bar', desc: 'Glass drink dispensers, garnish tray, matching napkins' },
  ];

  return (
    <section id="features" className="py-24 sm:py-32 px-4 sm:px-6 relative overflow-hidden" style={{ backgroundColor: 'var(--brand-cream)' }}>
      <div className="max-w-6xl mx-auto">

        {/* Section Eyebrow & Header */}
        <div className="text-center mb-16 sr-item" ref={reveal}>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase mb-3 bg-stone-100 text-stone-700 border border-stone-200">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Everything You Need
          </div>
          <h2
            className="font-extrabold leading-tight text-stone-950 tracking-tight"
            style={{ fontSize: 'clamp(2rem, 4.2vw, 3rem)' }}
          >
            Built for celebrations that feel effortless.
          </h2>
          <p className="mt-3 text-base sm:text-lg text-stone-500 max-w-xl mx-auto">
            From the initial spark to the last guest leaving, every detail is connected into one cohesive studio.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sr-stagger" ref={reveal}>

          {/* Bento Card 1: AI Theme Engine (2-column span on md+) */}
          <div className="md:col-span-2 card-editorial p-7 sm:p-8 flex flex-col justify-between overflow-hidden relative group">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
              <div>
                <span className="badge-stone text-[11px] mb-2.5 inline-block">Theme Engine</span>
                <h3 className="text-xl sm:text-2xl font-extrabold text-stone-900 tracking-tight">
                  Tailored Themes with Cohesive Color Systems
                </h3>
                <p className="text-sm text-stone-600 mt-2 max-w-md leading-relaxed">
                  Never settle for cookie-cutter party kits. We analyze your age group, venue, and vibe to generate 4 distinctly crafted themes with custom hex palettes.
                </p>
              </div>
              <div className="shrink-0 flex items-center gap-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200/80 px-3 py-1 rounded-full text-xs font-bold">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                <span>9.6 Fit Score</span>
              </div>
            </div>

            {/* Interactive Visual Preview inside card */}
            <div className="bg-stone-50/90 rounded-2xl p-4 sm:p-5 border border-stone-200/80 mt-2">
              <div className="flex items-center justify-between gap-3 mb-3">
                <span className="text-xs font-bold text-stone-800">Sunlit Conservatory Garden Brunch</span>
                <span className="text-[11px] text-stone-500 font-medium">Palette · 5 harmonious tones</span>
              </div>
              <div className="flex gap-2">
                {[
                  { hex: '#52796F', label: 'Sage' },
                  { hex: '#84A98C', label: 'Eucalyptus' },
                  { hex: '#CAD2C5', label: 'Mist' },
                  { hex: '#E07A5F', label: 'Terracotta' },
                  { hex: '#F4F1DE', label: 'Linen' },
                ].map((color) => (
                  <div key={color.hex} className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className="h-10 sm:h-12 w-full rounded-xl shadow-xs border border-white transition-transform group-hover:scale-[1.02]"
                      style={{ backgroundColor: color.hex }}
                    />
                    <span className="text-[10px] text-stone-500 font-medium hidden sm:inline">{color.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bento Card 2: Spatial Room Zoning (1-column) */}
          <div className="card-editorial p-7 sm:p-8 flex flex-col justify-between">
            <div>
              <span className="badge-stone text-[11px] mb-2.5 inline-block">Spatial Guide</span>
              <h3 className="text-xl font-extrabold text-stone-900 tracking-tight">
                4-Zone Decor Blueprint
              </h3>
              <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                Translate theme ideas into real room zones with step-by-step layout advice.
              </p>
            </div>

            {/* Interactive zone selector */}
            <div className="mt-5 space-y-2">
              {zones.map((zone) => (
                <button
                  key={zone.id}
                  onClick={() => setActiveZone(zone.id)}
                  className={`w-full text-left p-2.5 rounded-xl border text-xs font-semibold transition-all flex items-center justify-between ${
                    activeZone === zone.id
                      ? 'bg-stone-950 text-white border-stone-950 shadow-xs'
                      : 'bg-stone-50 hover:bg-stone-100 text-stone-700 border-stone-200/80'
                  }`}
                >
                  <span>{zone.label}</span>
                  <span className="text-[10px] opacity-70">
                    {activeZone === zone.id ? 'Active' : 'Preview'}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Bento Card 3: Real-Time Budget Analyzer (1-column) */}
          <div className="card-editorial p-7 sm:p-8 flex flex-col justify-between">
            <div>
              <span className="badge-stone text-[11px] mb-2.5 inline-block">Budget Health</span>
              <h3 className="text-xl font-extrabold text-stone-900 tracking-tight">
                Variance & Savings Engine
              </h3>
              <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                Keep spending predictable with live category breakdowns and Rent vs. Buy comparisons.
              </p>
            </div>

            <div className="bg-stone-50 rounded-2xl p-4 border border-stone-200/80 mt-5 space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-stone-700">Estimated Total</span>
                <span className="font-bold text-stone-950">$512 / $600</span>
              </div>
              <div className="w-full bg-stone-200 h-2 rounded-full overflow-hidden">
                <div className="bg-emerald-500 h-full rounded-full" style={{ width: '85%' }} />
              </div>
              <div className="flex items-center justify-between text-[11px] pt-1 border-t border-stone-200/60 text-stone-600">
                <span>Rent vs. Buy savings</span>
                <span className="font-bold text-emerald-700">Save $124</span>
              </div>
            </div>
          </div>

          {/* Bento Card 4: Run of Show & Timeline (2-column span on md+) */}
          <div className="md:col-span-2 card-editorial p-7 sm:p-8 flex flex-col justify-between">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-5">
              <div>
                <span className="badge-stone text-[11px] mb-2.5 inline-block">Timeline & Tasks</span>
                <h3 className="text-xl sm:text-2xl font-extrabold text-stone-900 tracking-tight">
                  Interactive Run of Show with Smart Reminders
                </h3>
                <p className="text-sm text-stone-600 mt-2 max-w-md leading-relaxed">
                  Phased checklists broken down by timing: 3 weeks out, 1 week before, day before, and event day.
                </p>
              </div>
            </div>

            {/* Interactive Timeline Preview */}
            <div className="bg-stone-50 rounded-2xl p-4 sm:p-5 border border-stone-200/80 space-y-2.5">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setCheckedTimelineItem(!checkedTimelineItem)}
                  className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${
                    checkedTimelineItem
                      ? 'bg-stone-950 border-stone-950 text-white'
                      : 'border-stone-300 bg-white'
                  }`}
                  aria-label="Toggle demo task"
                >
                  {checkedTimelineItem && (
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
                <span className={`text-xs sm:text-sm font-medium transition-all ${checkedTimelineItem ? 'line-through text-stone-400' : 'text-stone-800'}`}>
                  Confirm final guest count and rental drop-off window
                </span>
                <span className="ml-auto text-[10px] uppercase font-bold text-stone-500 bg-stone-200/70 px-2 py-0.5 rounded-full shrink-0">
                  1 Week Out
                </span>
              </div>
              <div className="flex items-center gap-3 opacity-60">
                <div className="w-5 h-5 rounded-md border border-stone-300 bg-white" />
                <span className="text-xs sm:text-sm font-medium text-stone-700">
                  Pick up fresh florals and chill white wine & beverages
                </span>
                <span className="ml-auto text-[10px] uppercase font-bold text-stone-500 bg-stone-200/70 px-2 py-0.5 rounded-full shrink-0">
                  Day Before
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
